import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../../Spinner';
import {ClientMemoryApi} from '../../../../api/clientMemoryApi';
import {copyPlainText} from '../../../../utils/copyPlainText';
import {
    buildContextPlainText, contextEyebrow, contextSections, isContextPayload,
} from './contextSheetText';

/**
 * La hoja "Como lo ve tu IA": el contexto literal que recibe el asistente.
 *
 * Lo que la hace valer es de DÓNDE sale el dato: `agent/get_client_context/` es el mismo endpoint
 * que consume el MCP público. Si esta hoja mostrara algo armado en el frontend (o scrapeado del
 * archivo, como en el mockup), diría lo que nosotros creemos que ve la IA en vez de lo que ve —
 * que es precisamente la pregunta que la agencia vino a contestar.
 *
 * Por eso también se pide SIN cache: se abre justo después de aprobar un hecho, y 30 segundos de
 * respuesta vieja esconderían exactamente lo que el usuario acaba de hacer.
 */

const STATE_LOADING = 'loading';
const STATE_READY = 'ready';
const STATE_FAILED = 'failed';

export function IaContextSheet({clientId}) {
    const {t} = useTranslation();
    const [context, setContext] = useState(null);
    const [status, setStatus] = useState(STATE_LOADING);

    useEffect(() => {
        let alive = true;
        setStatus(STATE_LOADING);
        ClientMemoryApi.getClientContext(clientId).then((data) => {
            if (!alive) return;
            // El endpoint devuelve el contexto CRUDO (sin envoltorio `{status, data}`): se detecta
            // por la presencia de `client_id`, no por un status que no viene.
            if (isContextPayload(data)) {
                setContext(data);
                setStatus(STATE_READY);
            } else {
                setStatus(STATE_FAILED);
            }
        }).catch(() => { if (alive) setStatus(STATE_FAILED); });
        return () => { alive = false; };
    }, [clientId]);

    const handleCopy = () => {
        copyPlainText(buildContextPlainText(context, t))
            .then(() => toast.success(t('ia_booster.iapaper.copied')))
            .catch(() => toast.error(t('ia_booster.iapaper.copy_error')));
    };

    if (status === STATE_LOADING) {
        return <div className="box-loader"><Spinner color="positive"/></div>;
    }
    if (status === STATE_FAILED) {
        // "No pude leer el contexto" NO es "tu IA no recibe nada": una hoja en blanco afirmaría lo
        // segundo, que es una mentira sobre el producto.
        return (
            <div className="iabm-paper -failed">
                <p className="iabm-paper__lede">{t('ia_booster.iapaper.load_error')}</p>
            </div>
        );
    }

    const sections = contextSections(context, t);

    return (
        <div className="iabm-paper">
            <button type="button" className="iabm-paper__copy" onClick={handleCopy}>
                {t('ia_booster.iapaper.copy')}
            </button>
            <div className="iabm-paper__eyebrow">{contextEyebrow(context, t)}</div>
            <h3 className="iabm-paper__title">{t('ia_booster.iapaper.title')}</h3>
            <p className="iabm-paper__lede">{t('ia_booster.iapaper.lede')}</p>
            {sections.length === 0 && (
                <p className="iabm-paper__empty">{t('ia_booster.iapaper.empty')}</p>
            )}
            {sections.map(section => (
                <section key={section.key} className="iabm-paper__section">
                    {section.title && <h4 className="iabm-paper__head">{section.title}</h4>}
                    {section.lines.map((line, index) => (
                        <p key={`${section.key}-${index}`} className="iabm-paper__line">{line}</p>
                    ))}
                    {section.note && <p className="iabm-paper__note">{section.note}</p>}
                </section>
            ))}
        </div>
    );
}

export default IaContextSheet;
